import React, { useMemo } from 'react';
import { downloadToExcel } from '../../../utils/ExportUtils';

const ModulePerformanceReport = ({ data }) => {
  const { companies, contacts, clients, jobs, candidates, tasks, employees, invoices } = data;

  const moduleData = useMemo(() => {
    const placements = invoices.filter((inv) => inv.candidateId || inv.candidate_id || inv.candidate).length;
    const interviewScheduled = invoices.filter((inv) => inv.status === 'INTERVIEW_SCHEDULED').length || 
                              candidates.filter((c) => c.status === 'INTERVIEW_SCHEDULED').length;
    const activeJobs = jobs.filter((j) => j.status?.toLowerCase() === 'open').length;
    const completedTasks = tasks.filter((t) => t.status === 'COMPLETED').length;
    const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const paidInvoices = invoices.filter((inv) => inv.status === 'PAID').length;

    // Employee Performance
    const employeeMetrics = employees.map((emp) => {
      const empId = emp._id || emp.id;
      const empContacts = contacts.filter((c) => c.assignedTo === empId || c.assigned_to === empId || c.createdBy === empId);
      const empClients = clients.filter((c) => c.assignedTo === empId || c.assigned_to === empId || c.createdBy === empId);
      const empInvoices = invoices.filter((inv) => inv.assignedTo === empId || inv.createdBy === empId);
      const empRevenue = empInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      return {
        name: emp.name || emp.email,
        contacts: empContacts.length,
        clients: empClients.length,
        revenue: (empRevenue / 100000).toFixed(2),
      };
    });

    return {
      crm: {
        title: 'CRM Performance',
        metrics: [
          { label: 'Contacts Created', value: contacts.length },
          { label: 'Clients Won', value: clients.length },
          { label: 'Conversion Rate (%)', value: contacts.length > 0 ? ((clients.length / contacts.length) * 100).toFixed(1) : 0 },
          { label: 'Companies Contacted', value: companies.length },
          { label: 'Follow-ups Done', value: completedTasks },
        ]
      },
      ats: {
        title: 'ATS Performance',
        metrics: [
          { label: 'Active Jobs (Status: Open)', value: activeJobs },
          { label: 'Candidates Submitted', value: candidates.length },
          { label: 'Interview Scheduled', value: interviewScheduled },
          { label: 'Placements Done', value: placements },
          { label: 'Placement Rate (%)', value: candidates.length > 0 ? ((placements / candidates.length) * 100).toFixed(1) : 0 },
        ]
      },
      finance: {
        title: 'Finance Performance',
        metrics: [
          { label: 'Invoices Raised', value: invoices.length },
          { label: 'Paid Invoices', value: paidInvoices },
          { label: 'Pending Invoices', value: invoices.filter((inv) => inv.status === 'PENDING').length },
          { label: 'Total Revenue (Lakhs)', value: (totalRevenue / 100000).toFixed(2) },
          { label: 'Collection Rate (%)', value: invoices.length > 0 ? ((paidInvoices / invoices.length) * 100).toFixed(1) : 0 },
        ]
      },
      employees: employeeMetrics.filter((e) => e.contacts > 0 || e.clients > 0 || e.revenue > 0).sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue)),
    };
  }, [companies, contacts, clients, jobs, candidates, tasks, employees, invoices]);

  const handleDownloadReport = () => {
    const rows = [
      // CRM Metrics
      ['CRM PERFORMANCE', ''],
      [],
      ['Metric', 'Value'],
      ['Contacts Created', moduleData.crm.metrics[0].value],
      ['Clients Won', moduleData.crm.metrics[1].value],
      ['Conversion Rate (%)', moduleData.crm.metrics[2].value],
      ['Companies Contacted', moduleData.crm.metrics[3].value],
      ['Follow-ups Done', moduleData.crm.metrics[4].value],
      [],

      // ATS Metrics
      ['ATS PERFORMANCE', ''],
      [],
      ['Metric', 'Value'],
      ['Active Jobs (Open)', moduleData.ats.metrics[0].value],
      ['Candidates Submitted', moduleData.ats.metrics[1].value],
      ['Interviews Scheduled', moduleData.ats.metrics[2].value],
      ['Placements Done', moduleData.ats.metrics[3].value],
      ['Placement Rate (%)', moduleData.ats.metrics[4].value],
      [],

      // Finance Metrics
      ['FINANCE PERFORMANCE', ''],
      [],
      ['Metric', 'Value'],
      ['Invoices Raised', moduleData.finance.metrics[0].value],
      ['Paid Invoices', moduleData.finance.metrics[1].value],
      ['Pending Invoices', moduleData.finance.metrics[2].value],
      ['Total Revenue (Lakhs)', moduleData.finance.metrics[3].value],
      ['Collection Rate (%)', moduleData.finance.metrics[4].value],
      [],

      // Employee Performance
      ['EMPLOYEE PERFORMANCE', ''],
      [],
      ['Employee Name', 'Contacts', 'Clients', 'Revenue (₹L)'],
      ...moduleData.employees.map((emp) => [emp.name, emp.contacts, emp.clients, emp.revenue]),
    ];

    downloadToExcel(rows, [], `Module_Performance_Report`);
  };

  return (
    <div className="report-section">
      <div className="report-header">
        <h2>📈 Module Performance Report</h2>
        <button className="btn-download excel" onClick={handleDownloadReport}>
          📥 Download Full Report
        </button>
      </div>

      {/* Module Metrics Cards */}
      <div className="modules-section">
        {/* CRM */}
        <div className="module-card">
          <div className="module-title">📞 CRM Performance</div>
          <div className="metrics-list">
            {moduleData.crm.metrics.map((metric, idx) => (
              <div key={idx} className="metric-row">
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">{metric.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ATS */}
        <div className="module-card">
          <div className="module-title">💼 ATS Performance</div>
          <div className="metrics-list">
            {moduleData.ats.metrics.map((metric, idx) => (
              <div key={idx} className="metric-row">
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">{metric.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Finance */}
        <div className="module-card">
          <div className="module-title">💰 Finance Performance</div>
          <div className="metrics-list">
            {moduleData.finance.metrics.map((metric, idx) => (
              <div key={idx} className="metric-row">
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">{metric.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employee Performance Table */}
      <div className="employee-performance-section">
        <h3>Team Performance (Employee-wise)</h3>
        <div className="employee-table">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Contacts Created</th>
                <th>Clients Won</th>
                <th>Revenue Generated (₹L)</th>
              </tr>
            </thead>
            <tbody>
              {moduleData.employees.length > 0 ? (
                moduleData.employees.map((emp, idx) => (
                  <tr key={idx}>
                    <td className="employee-name">{emp.name}</td>
                    <td className="center">{emp.contacts}</td>
                    <td className="center">{emp.clients}</td>
                    <td className="center highlight">₹{emp.revenue}L</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">No employee data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModulePerformanceReport;
